import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';

import BaseTable, { IBaseTableData, IBaseTableRow } from '../table/baseTable';
import { DeparturesDataItem, ShipsDataItem } from './interfaces/arrivalsDepartures.interfaces';
import StandardButton from '../btn-standard';

import { useDepartureData } from './hooks/useDepartureData';
import { useShipsData } from './hooks/useShipsData';
import LoadMoreButton from '../btnLoadMore';

// --------------------------------------
//  Interfaces
// --------------------------------------

export interface DeparturesProps {
  show: boolean;
  refresh: boolean;
  locale: string;
  fallbackImage: string | null;
  dateSpan: Date[] | undefined;
  onShowHistory: Function;
}

export interface ExpandedDeparturesDetailsProps {
  departureData: DeparturesDataItem;
  expanded: boolean;
  fallbackImage: string | null;
  onShowHistory: Function;
}

export interface ShipImageProps {
  url: string | null;
  show: boolean;
  fallbackImage: string | null;
}

// --------------------------------------
//  Headers, Columns - for the table
// --------------------------------------

const TableHeader = (headerText: string, className?: string) => <span className={className}>{headerText}</span>;
const RowColumn = (columnText: string, bold?: boolean) => (
  <div className={`arrivals-departures-column ${bold ? 'bold' : ''}`}>{columnText}</div>
);

// --------------------------------------
//  Ship image
// --------------------------------------

const ShipImage = ({ url, show, fallbackImage }: ShipImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      if (url || fallbackImage) {
        setImageUrl(url || fallbackImage);
      }
    }
  }, [url, show]);

  useEffect(() => {
    if (imageUrl) {
      let image = new Image();
      image.src = imageUrl;

      image.onload = () => {
        setLoaded(true);
      };

      image.onerror = () => {
        if (url || fallbackImage) {
          setImageUrl(fallbackImage);
        }
      };
    }
  }, [imageUrl]);

  return imageUrl ? (
    <div
      className={`arrivals-departures-ship-image ${loaded && show ? 'show' : ''}`}
      style={{ backgroundImage: `url(${imageUrl})` }}
    ></div>
  ) : null;
};

// --------------------------------------
// Status column
// --------------------------------------

const StatusColumn = ({ isActual, departureTime }: DeparturesDataItem) => {
  const [statusColor, setStatusColor] = useState('blue');
  const [statusText, setStatusText] = useState('');
  useEffect(() => {
    if (isActual) {
      setStatusText(`${t('arrivalsdepartures.departured')}`);
      setStatusColor('green');
    } else {
      if (dayjs().isAfter(dayjs(departureTime))) {
        setStatusText(`${t('arrivalsdepartures.delayed')}`);
        setStatusColor('red');
      } else {
        setStatusText(`${t('arrivalsdepartures.expectedDeparture')}`);
        setStatusColor('blue');
      }
    }
  }, [isActual]);

  return (
    <div className="status-column">
      <div className={`status-circle ${statusColor}`}></div>
      <div className="status-text">{statusText}</div>
    </div>
  );
};

// ----------------------------------------------------------------------------
//
//  EXPANDED DEPARTURE DETAILS COMPONENT
//
// ----------------------------------------------------------------------------

const ExpandedDeparturesDetails = ({
  expanded,
  departureData,
  onShowHistory,
  fallbackImage,
}: ExpandedDeparturesDetailsProps) => {
  const { shipData } = useShipsData(departureData.imo, expanded);

  return (
    <div className="arrivals-departures-ship-details">
      <div className="arrivals-departures-ship-details-content">
        <h3 className="ship-details-top-title">{`${t('arrivalsdepartures.departureTo')} ${
          departureData.nextPortName
        } ${t('arrivalsdepartures.fromQuay')} ${departureData.quay || '-'}`}</h3>
        <div className="arrivals-departures-ship-details-top">
          <div className="column-left">
            <ul>
              <li className="mobile-only">
                <h6>{t('arrivalsdepartures.quayName')}:</h6>
                <p>{`${departureData?.quayName || '-'}`}</p>
              </li>
              <li>
                <h6>{t('arrivalsdepartures.length')}:</h6>
                <p>{`${shipData?.length || '-'} meter`}</p>
              </li>

              <li>
                <h6>{t('arrivalsdepartures.width')}:</h6>
                <p>{`${shipData?.width || '-'} meter`}</p>
              </li>
              <li>
                <h6>{t('arrivalsdepartures.flag')}:</h6>
                <p>{shipData?.flag || '-'}</p>
              </li>
            </ul>
          </div>
          <div className="column-right">
            <ul>
              <li>
                <h6>{t('arrivalsdepartures.imoNumber')}:</h6>
                <p>{departureData?.imo || '-'}</p>
              </li>
              <li className="mobile-only">
                <h6>{t('arrivalsdepartures.agentName')}:</h6>
                <p>{departureData?.agentName || '-'}</p>
              </li>
              <li>
                <h6>{t('arrivalsdepartures.callSign')}:</h6>
                <p>{shipData?.callSign || '-'}</p>
              </li>
              <li className="mobile-only">
                <h6>{t('arrivalsdepartures.status')}:</h6>
                <StatusColumn {...departureData} />
              </li>
            </ul>
          </div>
        </div>

        <div className="arrivals-departures-ship-details-bottom">
          <StandardButton
            text={t('arrivalsdepartures.showDepartureHistory')}
            disabled={!departureData?.imo || !shipData}
            onClick={() => onShowHistory(shipData)}
            isWhite={false}
          />
        </div>
      </div>
      <ShipImage url={null} show={expanded} fallbackImage={fallbackImage} />
    </div>
  );
};

// ----------------------------------------------------------------------------
//
//  DEPARTURES COMPONENT
//
// ----------------------------------------------------------------------------

const Departures = ({ show, locale, onShowHistory, refresh, fallbackImage, dateSpan }: DeparturesProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [refreshList, setRefreshList] = useState(false);
  const [loadMore, setLoadMore] = useState(false);
  const { departuresList, noMoreToLoad } = useDepartureData(refreshList, loadMore, dateSpan);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  useEffect(() => {
    if (refresh) {
      setRefreshList(true);
    }
  }, [refresh]);

  useEffect(() => {
    setRefreshList(true);
  }, [dateSpan]);

  // --------------------------------------
  //  Set language/locale
  // --------------------------------------

  useEffect(() => {
    if (locale) {
      dayjs.locale(locale);
    }
  }, [locale]);

  const { t } = useTranslation();

  // --------------------------------------
  //  Set table data - useMemo
  // --------------------------------------

  const tableData = useMemo(() => {
    const baseData: IBaseTableData = {
      headers: [
        { id: 1, headerData: TableHeader(t('arrivalsdepartures.ship')), columnId: 1 },
        { id: 2, headerData: TableHeader(t('arrivalsdepartures.departureTime')), columnId: 2 },
        { id: 3, headerData: TableHeader(t('arrivalsdepartures.quayName')), columnId: 3, hideOnMobile: true },
        {
          id: 4,
          headerData: TableHeader(t('arrivalsdepartures.agentName')),
          hideOnMobile: true,
          columnId: 4,
        },
        {
          id: 5,
          headerData: TableHeader(t('arrivalsdepartures.status')),
          hideOnMobile: true,
          columnId: 5,
        },
      ],
      rows: [],
    };
    if (departuresList) {
      setLoadMore(false);
      setRefreshList(false);

      departuresList.forEach((data, index) => {
        const rowData: IBaseTableRow = {
          id: index,
          columns: [
            { id: 1, columnData: RowColumn(data.name, true), filterValue: data.name },
            {
              id: 2,
              columnData: RowColumn(dayjs(data.departureTime).format('YYYY-MM-DD HH:mm')),
              filterValue: dayjs().valueOf() - dayjs(data.departureTime).valueOf(),
            },
            {
              id: 3,
              columnData: RowColumn(data.quayName.toLowerCase()),
              filterValue: data.quayName.toLowerCase(),
              hideOnMobile: true,
            },
            {
              id: 4,
              columnData: RowColumn(data.agentName),
              hideOnMobile: true,
              filterValue: data.agentName,
            },
            {
              id: 5,
              columnData: <StatusColumn {...data} />,
              hideOnMobile: true,
              filterValue: t('arrivalsdepartures.departured'),
            },
          ],
          expandedContent: (
            <ExpandedDeparturesDetails
              departureData={data}
              expanded={expandedRows.some((r) => r === index)}
              fallbackImage={fallbackImage}
              onShowHistory={(shipData: ShipsDataItem) => {
                onShowHistory(shipData, data);
              }}
            />
          ),
          expandedAtStart: false,
          data,
        };

        baseData?.rows.push(rowData);
      });
    }

    return baseData;
  }, [departuresList, JSON.stringify(expandedRows)]);

  const onRowExpanded = (row: IBaseTableRow) => {
    expandedRows.push(row.id);
    setExpandedRows([...expandedRows]);
  };

  const onRowClosed = (row: IBaseTableRow) => {
    const i = expandedRows.findIndex((rowId) => rowId === row.id);
    if (i >= 0) {
      expandedRows.splice(i, 1);
      setExpandedRows([...expandedRows]);
    }
  };

  const loadMoreItems = () => {
    setLoadMore(true);
  };

  // --------------------------------------
  //  JSX
  // --------------------------------------

  return show ? (
    <div ref={ref} className="departures-table">
      <BaseTable
        data={tableData}
        onExpanded={(row: IBaseTableRow) => onRowExpanded(row)}
        onClosed={(row: IBaseTableRow) => onRowClosed(row)}
        defaultFilter={{ id: 2, descending: false }}
      />
      {departuresList.length > 0 && !noMoreToLoad ? (
        <div className="arrivals-departures-load-more">
          <LoadMoreButton
            className="arrivals-departures-load-more-button"
            text={loadMore ? t('arrivalsdepartures.loading') : t('arrivalsdepartures.loadMore')}
            onClick={() => loadMoreItems()}
            isLoading={loadMore}
          />
        </div>
      ) : null}
    </div>
  ) : null;
};

export default Departures;
